import type {BillingTermsFormValues} from '../jobs/types'
import {createUuid} from '../lib/create-uuid'
import type {ProjectBillingMethod} from '../projects/schemas'

export type QuoteUnit = 'day' | 'hr' | 'ea' | 'gal' | 'bbl' | 'ft' | 'ton' | 'wk' | 'mo' | 'load'

export type ChargeType = 'disposal' | 'washout' | 'hsei' | 'transportation'

export type QuoteItemLine = {
    kind: 'item'
    id: string
    description: string
    quantity: number
    duration: number | null
    unit: QuoteUnit
    rate: number
    chargeType?: ChargeType
    forRentalPeriod?: boolean
}

export type QuotePercentLine = {
    kind: 'percent'
    id: string
    description: string
    rate: number
    chargeType?: ChargeType
}

export type QuoteLine = QuoteItemLine | QuotePercentLine

export type QuoteRequirementKind = 'worker' | 'equipment'

export const QUOTE_REQUIREMENT_KIND_LABELS: Record<QuoteRequirementKind, string> = {
    worker: 'Workers',
    equipment: 'Equipment',
}

export type QuoteWorkerRequirement = {
    id: string
    lineId: string
    kind: 'worker'
    workerTypeId: string
    qty: number
}

export type QuoteEquipmentRequirement = {
    id: string
    lineId: string
    kind: 'equipment'
    rentalAssetTypeId: string
    qty: number
}

export type QuoteRequirement = QuoteWorkerRequirement | QuoteEquipmentRequirement

export type QuoteRentalPeriod =
    | {kind: 'months'; months: number}
    | {kind: 'dates'; startDate: Date; endDate: Date}

export type QuoteMeta = {
    name: string
    customerId: string | null
    customerContactId: string | null
    poNumber: string
    terms: string
    dueDate: Date | null
    rentalPeriod?: QuoteRentalPeriod | null
}

export type QuoteStatus = import('./schemas').QuoteStatus

export type QuoteDraft = {
    meta: QuoteMeta
    status: QuoteStatus
    lines: QuoteLine[]
    requirements: QuoteRequirement[]
}

export type BuildPattern = 'one_day' | 'deploy_pickup' | 'deploy_service_pickup'

export type ServiceCadence = {
    weekdays: number[]
    everyNWeeks: 1
}

export type ServiceTemplateRequirement = {
    id: string
    kind: QuoteRequirementKind
    typeId: string
    qty: number
}

export type Scheduling = {
    pattern: BuildPattern
    startDate: Date
    endDate: Date
    startTime?: string | null
    endTime?: string | null
    cadence: ServiceCadence
    serviceTemplate: ServiceTemplateRequirement[]
    serviceTypeId: string | null
    serviceNotes: string
    deployNotes: string
    pickupNotes: string
    skippedServiceDates: string[]
    markJobsReadyForDispatch?: boolean
}

export type GeneratedRequirement = {
    kind: QuoteRequirementKind
    typeId: string
    qty: number
}

export type GeneratedJobStage = 'deploy' | 'service' | 'pickup'

export type GeneratedJob = {
    id: string
    stage: GeneratedJobStage
    date: Date
    label: string
    requirements: GeneratedRequirement[]
    visitIndex?: number
    skipped?: boolean
}

export const QUOTE_UNITS: QuoteUnit[] = [
    'day',
    'hr',
    'ea',
    'gal',
    'bbl',
    'ft',
    'ton',
    'wk',
    'mo',
    'load',
]

export const QUOTE_UNIT_META: Record<QuoteUnit, {label: string; plural: string}> = {
    day: {label: 'day', plural: 'days'},
    hr: {label: 'hr', plural: 'hrs'},
    ea: {label: 'ea', plural: 'ea'},
    gal: {label: 'gal', plural: 'gal'},
    bbl: {label: 'bbl', plural: 'bbl'},
    ft: {label: 'ft', plural: 'ft'},
    ton: {label: 'ton', plural: 'tons'},
    wk: {label: 'wk', plural: 'wks'},
    mo: {label: 'mo', plural: 'mo'},
    load: {label: 'load', plural: 'loads'},
}

function toHundredths(value: number): number {
    return Math.round(value * 100)
}

/** Divide `units` by `divisor` with round-half-to-even, matching Python Decimal. */
function divideHalfEven(units: bigint, divisor: bigint): number {
    const negative = units < 0n
    const magnitude = negative ? -units : units
    let quotient = magnitude / divisor
    const twiceRemainder = 2n * (magnitude - quotient * divisor)
    if (twiceRemainder > divisor || (twiceRemainder === divisor && quotient % 2n === 1n)) {
        quotient += 1n
    }
    return Number(negative ? -quotient : quotient)
}

function createLineId(): string {
    return createUuid()
}

export function unitLabel(unit: string, quantity: number): string {
    const meta = QUOTE_UNIT_META[unit as QuoteUnit]
    if (meta == null) {
        return unit
    }
    return quantity === 1 ? meta.label : meta.plural
}

/** Singular unit label for rate suffixes; falls back to the raw unit string. */
export function unitSingularLabel(unit: string): string {
    return QUOTE_UNIT_META[unit as QuoteUnit]?.label ?? unit
}

// Cycle length in days for each period-priced unit
export const RENTAL_PERIOD_UNIT_DAYS: Partial<Record<QuoteUnit, number>> = {day: 1, wk: 7, mo: 28}

export const DAYS_PER_MONTH_WITHOUT_START = 30

export function isRentalPeriodUnit(unit: QuoteUnit): boolean {
    return RENTAL_PERIOD_UNIT_DAYS[unit] != null
}

// Days since the epoch for the calendar date
function toWholeDayNumber(date: Date): number {
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86_400_000
}

export function rentalPeriodDays(period: QuoteRentalPeriod): number {
    if (period.kind === 'months') {
        return period.months * DAYS_PER_MONTH_WITHOUT_START
    }

    return toWholeDayNumber(period.endDate) - toWholeDayNumber(period.startDate) + 1
}

export function applyRentalPeriod(
    lines: QuoteLine[],
    previous: QuoteRentalPeriod | null | undefined,
    next: QuoteRentalPeriod | null | undefined,
    untickedLineIds: ReadonlySet<string>,
): QuoteLine[] {
    if (isValidRentalPeriod(previous) || !isValidRentalPeriod(next)) {
        return lines
    }

    return lines.map((line) =>
        line.kind === 'item' &&
        isRentalPeriodUnit(line.unit) &&
        line.duration == null &&
        !untickedLineIds.has(line.id)
            ? {...line, forRentalPeriod: true}
            : line,
    )
}

export function isValidRentalPeriod(period: QuoteRentalPeriod | null | undefined): boolean {
    if (period == null) {
        return false
    }
    if (period.kind === 'months') {
        return Number.isInteger(period.months) && period.months > 0
    }

    return rentalPeriodDays(period) >= 1
}

export function rentalPeriodCount(
    unit: QuoteUnit,
    period: QuoteRentalPeriod | null | undefined,
): number | null {
    const unitDays = RENTAL_PERIOD_UNIT_DAYS[unit]
    if (unitDays == null || period == null || !isValidRentalPeriod(period)) {
        return null
    }
    return Math.max(1, Math.ceil(rentalPeriodDays(period) / unitDays))
}

export function rentalPeriodBasis(
    unit: QuoteUnit,
    period: QuoteRentalPeriod | null | undefined,
): string | null {
    const count = rentalPeriodCount(unit, period)
    const cycleDays = RENTAL_PERIOD_UNIT_DAYS[unit]
    if (count == null || cycleDays == null || period == null) {
        return null
    }
    if (unit === 'day') {
        return `${count} ${count === 1 ? 'day' : 'days'}`
    }

    const basis = `${count} × ${cycleDays}-day ${count === 1 ? 'cycle' : 'cycles'}`
    return rentalPeriodDays(period) < cycleDays ? `${basis} (${cycleDays}-day minimum)` : basis
}

export function linePeriodCount(
    line: QuoteLine,
    period: QuoteRentalPeriod | null | undefined,
): number | null {
    if (line.kind !== 'item' || !line.forRentalPeriod) {
        return null
    }

    return rentalPeriodCount(line.unit, period)
}

export function lineMultiplier(
    line: QuoteItemLine,
    period: QuoteRentalPeriod | null | undefined,
): number {
    return linePeriodCount(line, period) ?? line.duration ?? 1
}

// Exact line amount in millionths (hundredths of quantity, multiplier and rate)
function itemAmountUnits(line: QuoteItemLine, period: QuoteRentalPeriod | null | undefined) {
    return (
        BigInt(toHundredths(line.quantity)) *
        BigInt(toHundredths(lineMultiplier(line, period))) *
        BigInt(toHundredths(line.rate))
    )
}

function computeLineCents(
    line: QuoteLine,
    itemSubtotal: number,
    period?: QuoteRentalPeriod | null,
) {
    if (line.kind === 'item') {
        return divideHalfEven(itemAmountUnits(line, period), 10_000n)
    }

    return divideHalfEven(
        BigInt(toHundredths(line.rate)) * BigInt(toHundredths(itemSubtotal)),
        10_000n,
    )
}

export function lineExtended(
    line: QuoteLine,
    itemSubtotal: number,
    period?: QuoteRentalPeriod | null,
): number {
    return computeLineCents(line, itemSubtotal, period) / 100
}

export function itemSubtotal(lines: QuoteLine[], period?: QuoteRentalPeriod | null): number {
    const units = lines
        .filter((line): line is QuoteItemLine => line.kind === 'item')
        .reduce((sum, line) => sum + itemAmountUnits(line, period), 0n)
    return divideHalfEven(units, 10_000n) / 100
}

export function quoteSubtotal(lines: QuoteLine[], period?: QuoteRentalPeriod | null): number {
    const itemTotal = itemSubtotal(lines, period)
    return lines.reduce((sum, line) => sum + computeLineCents(line, itemTotal, period), 0) / 100
}

export function quoteTotal(lines: QuoteLine[], period?: QuoteRentalPeriod | null): number {
    return quoteSubtotal(lines, period)
}

export function lineRatesHint(lines: QuoteLine[], period?: QuoteRentalPeriod | null): string {
    const itemLines = lines.filter((line): line is QuoteItemLine => line.kind === 'item')
    if (itemLines.length === 0) {
        return 'Qty × duration × rate. Set the unit on each row (/ day, / mo, / ea)'
    }

    const parts = itemLines.map((line) => {
        const multiplier = linePeriodCount(line, period) ?? line.duration
        const quantity =
            multiplier == null
                ? line.quantity.toLocaleString('en-US')
                : `${line.quantity.toLocaleString('en-US')} × ${multiplier.toLocaleString('en-US')}`
        const rate = line.rate.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
        })
        return `${quantity} × ${rate} / ${unitSingularLabel(line.unit)}`
    })

    const preview = parts.slice(0, 3).join(', ')
    return `Qty × duration × rate · ${parts.length > 3 ? `${preview}, …` : preview}`
}

export function addItemLine(lines: QuoteLine[]): QuoteLine[] {
    return [
        ...lines,
        {
            kind: 'item',
            id: createLineId(),
            description: '',
            quantity: 1,
            duration: null,
            unit: 'ea',
            rate: 0,
        },
    ]
}

export function addPercentLine(lines: QuoteLine[]): QuoteLine[] {
    return [
        ...lines,
        {
            kind: 'percent',
            id: createLineId(),
            description: '',
            rate: 0,
        },
    ]
}

const SCALAR_CHARGE_TYPES: ChargeType[] = ['washout', 'hsei', 'transportation']

export function chargeTypeAvailable(lines: QuoteLine[], chargeType: ChargeType): boolean {
    if (!SCALAR_CHARGE_TYPES.includes(chargeType)) {
        return true
    }

    return !lines.some((line) => line.chargeType === chargeType)
}

export function addDisposalLine(lines: QuoteLine[]): QuoteLine[] {
    return [
        ...lines,
        {
            kind: 'item',
            id: createLineId(),
            description: 'Disposal',
            quantity: 1,
            duration: null,
            unit: 'gal',
            rate: 0,
            chargeType: 'disposal',
        },
    ]
}

export function addWashoutLine(lines: QuoteLine[]): QuoteLine[] {
    return [
        ...lines,
        {
            kind: 'item',
            id: createLineId(),
            description: 'Washout',
            quantity: 1,
            duration: null,
            unit: 'ea',
            rate: 0,
            chargeType: 'washout',
        },
    ]
}

export function addTransportationLine(lines: QuoteLine[], hourlyRate: number): QuoteLine[] {
    return [
        ...lines,
        {
            kind: 'item',
            id: createLineId(),
            description: 'Hourly transportation',
            quantity: 1,
            duration: null,
            unit: 'hr',
            rate: hourlyRate,
            chargeType: 'transportation',
        },
    ]
}

export function addTransportationPerLoadLine(lines: QuoteLine[]): QuoteLine[] {
    return [
        ...lines,
        {
            kind: 'item',
            id: createLineId(),
            description: 'Transportation per load',
            quantity: 1,
            duration: null,
            unit: 'load',
            rate: 0,
            chargeType: 'transportation',
        },
    ]
}

export function addHseiLine(lines: QuoteLine[]): QuoteLine[] {
    return [
        ...lines,
        {
            kind: 'percent',
            id: createLineId(),
            description: 'HSEI',
            rate: 0,
            chargeType: 'hsei',
        },
    ]
}

const DISPOSAL_UNIT_BY_QUOTE_UNIT: Partial<Record<QuoteUnit, string>> = {
    gal: 'gallon',
    bbl: 'barrel',
    ton: 'ton',
    ea: 'each',
    load: 'load',
}

type QuoteBillingTermsPrefill = Pick<
    BillingTermsFormValues,
    | 'disposalFees'
    | 'washoutFee'
    | 'hseiPercent'
    | 'transportationHourlyRate'
    | 'estimatedTransportationHours'
    | 'ratePerLoad'
>

const isTransportationLine = (line: QuoteLine): line is QuoteItemLine =>
    line.chargeType === 'transportation' && line.kind === 'item'

export function quoteBillingMethod(lines: QuoteLine[]): ProjectBillingMethod {
    const transportation = lines.find(isTransportationLine)
    if (transportation == null) {
        return 'line_rates'
    }

    return transportation.unit === 'load' ? 'per_load' : 'hourly_transportation'
}

export function quoteLinesToBillingTerms(lines: QuoteLine[]): QuoteBillingTermsPrefill {
    const prefill: QuoteBillingTermsPrefill = {
        disposalFees: [],
        washoutFee: null,
        hseiPercent: null,
        transportationHourlyRate: null,
        estimatedTransportationHours: null,
        ratePerLoad: null,
    }

    for (const line of lines) {
        if (line.chargeType === 'disposal' && line.kind === 'item') {
            prefill.disposalFees.push({
                wasteType: line.description,
                unit: DISPOSAL_UNIT_BY_QUOTE_UNIT[line.unit] ?? '',
                unitRate: line.rate,
                estimatedQuantity: line.quantity * (line.duration ?? 1),
            })
        } else if (line.chargeType === 'washout' && line.kind === 'item') {
            prefill.washoutFee ??= line.rate
        } else if (line.chargeType === 'hsei' && line.kind === 'percent') {
            prefill.hseiPercent ??= line.rate
        } else if (isTransportationLine(line)) {
            if (line.unit === 'load') {
                prefill.ratePerLoad ??= line.rate
            } else {
                prefill.transportationHourlyRate ??= line.rate
                prefill.estimatedTransportationHours ??= line.quantity * (line.duration ?? 1)
            }
        }
    }

    return prefill
}

export function removeLine(lines: QuoteLine[], lineId: string): QuoteLine[] {
    return lines.filter((line) => line.id !== lineId)
}

export function moveLine(lines: QuoteLine[], fromIndex: number, toIndex: number): QuoteLine[] {
    if (
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= lines.length ||
        toIndex >= lines.length
    ) {
        return lines
    }

    const next = [...lines]
    const [moved] = next.splice(fromIndex, 1)
    if (moved == null) {
        return lines
    }
    next.splice(toIndex, 0, moved)
    return next
}

export function updateLine(
    lines: QuoteLine[],
    lineId: string,
    patch:
        | Partial<Omit<QuoteItemLine, 'kind' | 'id'>>
        | Partial<Omit<QuotePercentLine, 'kind' | 'id'>>,
): QuoteLine[] {
    return lines.map((line) => {
        if (line.id !== lineId) {
            return line
        }

        if (
            line.kind === 'item' &&
            ('quantity' in patch ||
                'duration' in patch ||
                'unit' in patch ||
                'rate' in patch ||
                'description' in patch ||
                'forRentalPeriod' in patch)
        ) {
            return {...line, ...patch}
        }

        if (line.kind === 'percent' && ('rate' in patch || 'description' in patch)) {
            return {...line, ...patch}
        }

        return line
    })
}
